"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, Sun, Sparkles, CloudSun } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: 1,
  title: "Dasara Compression & Peak Spikes",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Manage sharp leisure spikes during the Dasara festival and winter holidays alongside midweek shoulder dips. We build dynamic minimum length-of-stay controls and pace-based pricing so your property maximizes high-demand yield.",
  metricLabel: "Strategy focus",
  metricValue: "Pace-based pricing & minimum stay restrictions"
}, {
  id: 2,
  title: "Bengaluru Weekend Rate Volatility",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Track rapid rate shifts driven by weekend drive-in demand across MakeMyTrip, Agoda, and Booking.com. Automated competitive alerts and yield adjustments protect RevPAR and secure high-intent bookings.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA monitoring & rule-based rate fencing"
}, {
  id: 3,
  title: "OTA Commission & Direct Booking Margins",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Eliminate unauthorized wholesaler rate leakage and reclaim profit margins lost to steep OTA commissions. We enforce rate parity across meta-search channels while driving direct conversions for boutique stays.",
  metricLabel: "Strategy focus",
  metricValue: "Wholesaler rate fencing & direct channel boost"
}];
const seasonalityPeriods = [{
  title: "Peak Demand (Dasara & Winter Holidays)",
  timing: "October – January",
  icon: Sparkles,
  highlight: "100% Compression & Max ADR",
  description: "Capitalize on peak cultural tourism and long holiday weekends with non-refundable rate tiers, minimum length-of-stay (MLOS) fences, and early-bird yield multipliers."
}, {
  title: "Shoulder Season (Weekend Getaways)",
  timing: "February – May & August – September",
  icon: Sun,
  highlight: "Weekend Drive-in Yielding",
  description: "Capture high-yield Bengaluru weekend transit traffic with dynamic 2-night packages, dining add-on bundles, and proactive Sunday check-out extensions."
}, {
  title: "Midweek & Monsoon Base Load",
  timing: "June – July & Weekday Travel",
  icon: CloudSun,
  highlight: "MICE & Corporate Base Occupancy",
  description: "Fill inventory during low-velocity periods through targeted corporate tie-ups, heritage tour groups, and direct booking incentives that maintain RevPAR stability."
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapemysore" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Mysore
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Mysore presents a distinct hospitality ecosystem driven by intense festive compression during Dasara, recurring weekend leisure travel from Bengaluru, and competitive midweek rates. Sunil Singh builds bespoke revenue frameworks engineered to maximize ADR while securing reliable baseline occupancy year-round.
          </p>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Seasonality Block */}
        <div className="mt-16 md:mt-20 rounded-xl border border-border bg-card p-6 sm:p-8 lg:p-10 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8 pb-6 border-b border-border">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                Demand Calendar
              </span>
              <h3 className="font-serif text-2xl sm:text-3xl font-bold text-foreground mt-1">
                Mysore Hospitality Seasonality Matrix
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              Targeted pricing strategies customized for every demand phase across the calendar year in Mysore.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {seasonalityPeriods.map((period, idx) => {
            const IconComp = period.icon;
            return <div key={idx} data-index={idx} className="rounded-lg border border-border/80 bg-background/50 p-5 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 rounded-md bg-secondary/15 text-secondary-foreground">
                        <IconComp className="size-5 text-chart-2" />
                      </div>
                      <div>
                        <span className="text-xs font-mono font-medium text-secondary">
                          {period.timing}
                        </span>
                        <h4 className="text-base font-semibold text-foreground">
                          {period.title}
                        </h4>
                      </div>
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mt-2">
                      {period.description}
                    </p>
                  </div>
                  <div className="mt-4 pt-3 border-t border-border/60">
                    <span className="inline-block text-xs font-semibold text-foreground">
                      {period.highlight}
                    </span>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Mysore hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}